import React, { useState, useEffect, useMemo } from 'react';
import { fetchReport } from '../services/api';
import ReportTable, { BooleanCell, NumberCell, ProductLink, LinkCell } from './ReportTable';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';

const GenericReport = ({ reportType, title }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchReport(reportType);
      setData(result);
    } catch (err) {
      setError('Αποτυχία φόρτωσης δεδομένων');
      console.error('Error loading report:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [reportType]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'productname',
        header: 'Προϊόν',
        cell: ({ row }) => (
          <ProductLink id={row.original.id} name={row.original.productname} />
        ),
      },
      {
        accessorKey: 'qtyinstock',
        header: 'Ποσ.',
        cell: ({ row }) => 
          row.original.skroutzUrl ? (
            <LinkCell href={row.original.skroutzUrl}>{row.original.qtyinstock}</LinkCell>
          ) : (
            row.original.qtyinstock
          ),
      },
      {
        accessorKey: 'freeExpected',
        header: 'Ελ.Αν.Τεμ',
        cell: ({ row }) =>
          row.original.sfUrl ? (
            <LinkCell href={row.original.sfUrl}>{row.original.freeExpected}</LinkCell>
          ) : (
            row.original.freeExpected
          ),
      },
      {
        accessorKey: 'stockPrice',
        header: 'T.A Υπαρ.',
        cell: ({ getValue }) => <NumberCell value={getValue()} />,
      },
      {
        accessorKey: 'vendorPrice',
        header: 'Τιμή Αγ.',
        cell: ({ getValue }) => <NumberCell value={getValue()} />,
      },
      {
        accessorKey: 'diffPrice',
        header: 'Διαφορά',
        cell: ({ getValue }) => {
          const val = getValue();
          const colorClass = val > 0 ? 'text-green-600' : val < 0 ? 'text-red-600' : '';
          return <span className={colorClass}><NumberCell value={val} /></span>;
        },
      },
      {
        accessorKey: 'sales',
        header: 'Πωλ.',
      },
      {
        accessorKey: 'eSales30',
        header: 'Πωλ30',
      },
      {
        accessorKey: 'eSales60',
        header: 'Πωλ60',
      },
      {
        accessorKey: 'salesMarketPlace',
        header: 'Skroutz',
      },
      {
        accessorKey: 'toxic',
        header: 'Τοξικό',
        cell: ({ getValue }) => <BooleanCell value={getValue()} />,
      },
      {
        accessorKey: 'toxicx2',
        header: 'Τοξ.Εμπ.',
        cell: ({ getValue }) => <BooleanCell value={getValue()} />,
      },
      {
        accessorKey: 'minimumPrice',
        header: 'Κόστος',
        cell: ({ getValue }) => <BooleanCell value={getValue()} />,
      },
      {
        accessorKey: 'category',
        header: 'Κατηγορία',
      },
      {
        accessorKey: 'problemPercentage',
        header: 'Προβλ%',
        cell: ({ getValue }) => <NumberCell value={getValue()} />,
      },
    ],
    []
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Φόρτωση αναφοράς...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="ml-2 text-red-700">{error}</span>
          <button
            onClick={loadData}
            className="ml-4 flex items-center rounded bg-red-100 px-3 py-1 text-sm text-red-700 hover:bg-red-200"
          >
            <RefreshCw className="mr-1 h-4 w-4" />
            Επανάληψη
          </button>
        </div>
      </div>
    );
  }

  // Calculate totals
  const totalValue = data.reduce((sum, item) => sum + (item.vendorPrice || 0), 0);
  const totalItems = data.reduce((sum, item) => sum + (item.qtyinstock || 0), 0);

  return (
    <div>
      {/* Summary stats */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
          <div className="text-sm text-blue-700">Προϊόντα</div>
          <div className="text-2xl font-bold text-blue-800">{data.length}</div>
        </div>
        <div className="rounded-lg bg-green-50 border border-green-200 p-4">
          <div className="text-sm text-green-700">Τεμάχια</div>
          <div className="text-2xl font-bold text-green-800">{totalItems}</div>
        </div>
        <div className="rounded-lg bg-purple-50 border border-purple-200 p-4">
          <div className="text-sm text-purple-700">Αξία</div>
          <div className="text-2xl font-bold text-purple-800">
            {new Intl.NumberFormat('el-GR', { minimumFractionDigits: 2 }).format(totalValue)} €
          </div>
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <button
          onClick={loadData}
          className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Ανανέωση
        </button>
      </div>
      <ReportTable data={data} columns={columns} title={title} />
    </div>
  );
};

export default GenericReport;
